import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../lib/useAuth'
import { supabase } from '../lib/supabaseClient'
import { isPlatformAdmin, isPlatformOrGlobalGm } from '../lib/roleHelpers'

// Bug 36: route-level guard for GM / admin surfaces. ProtectedRoute alone
// only enforces "must be signed in" — without this, a plain player could
// hit /dashboard or /org by typing the URL and the page rendered the
// command-center UI before any inner permission check fired.
//
// Access tiers:
//   - platformOnly: platform_role / users.role='m55_admin' only (Admin.jsx)
//   - default (GM): platform staff, global GM, ANY org_owner/admin/gm row,
//     or ANY event_players row with event_role IN (gm, co_gm)
//
// Falls back to /play when the check fails so a player is funnelled into
// their actual surface instead of dumped on /auth.
export default function GMRoute({ children, platformOnly = false }) {
  const { user, profile, loading } = useAuth()
  const [checking, setChecking] = useState(true)
  const [hasAccess, setHasAccess] = useState(false)

  useEffect(() => {
    if (loading) return
    if (!user) { setChecking(false); return }

    // Platform-only routes shortcut on the profile bridge.
    if (platformOnly) {
      setHasAccess(isPlatformAdmin(profile))
      setChecking(false)
      return
    }

    // GM tier — short-circuit when the profile already proves it.
    if (isPlatformOrGlobalGm(profile)) {
      setHasAccess(true)
      setChecking(false)
      return
    }

    let cancelled = false
    ;(async () => {
      // Parallel: any event_players gm/co_gm row OR any org_members
      // owner/admin/gm row. Either confirms GM-tier access.
      const [{ data: epRows }, { data: omRows }] = await Promise.all([
        // Bug 61 re-open — prod event_players has no `id` column (dev
        // drift). Use user_id for the existence check so the query
        // doesn't 400 on prod and trap co_gm users on /play.
        supabase
          .from('event_players').select('user_id')
          .eq('user_id', user.id)
          .in('event_role', ['gm', 'co_gm']).limit(1),
        supabase
          .from('org_members').select('user_id')
          .eq('user_id', user.id)
          .in('org_role', ['org_owner', 'org_admin', 'org_gm']).limit(1),
      ])
      if (cancelled) return
      setHasAccess(!!epRows?.[0] || !!omRows?.[0])
      setChecking(false)
    })()
    return () => { cancelled = true }
  }, [user?.id, profile?.role, profile?.platform_role, platformOnly, loading])

  if (loading || checking) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0e1116',
        color: '#9aa6b0',
        fontFamily: "'Saira Condensed', 'Rajdhani', sans-serif",
        fontSize: '13px',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
      }}>
        Authenticating…
      </div>
    )
  }

  if (!user) return <Navigate to="/auth" replace />
  if (!hasAccess) return <Navigate to="/play" replace />
  return children
}
